import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/** Human-readable labels for route segments */
const SEGMENT_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    customers: 'Customers',
    users: 'Users',
    institutions: 'Institutions',
    transactions: 'Transactions',
    settings: 'Settings',
    loans: 'Loans',
    pipeline: 'Pipeline',
    applications: 'Applications',
    'credit-decisioning': 'Credit Decisioning',
    disbursement: 'Disbursement',
    accounts: 'Accounts',
    collections: 'Collections',
    portfolio: 'Portfolio Risk',
    products: 'Products',
    reports: 'Reports',
    openbanking: 'Open Banking',
    brands: 'Brands',
    webhooks: 'Webhooks',
    analytics: 'Analytics',
    insurance: 'Insurance',
    mortgages: 'Mortgages',
    affordability: 'Affordability',
    kyc: 'KYC Management',
    reviews: 'Reviews',
    compliance: 'Compliance',
    alerts: 'Alert Management',
    sar: 'SAR Filing',
    'regulatory-returns': 'Regulatory Returns',
    sanctions: 'Sanctions Screening',
    audit: 'Audit Trail',
    log: 'Event Log',
    'general-ledger': 'General Ledger',
    journals: 'Journal Entries',
    'trial-balance': 'Trial Balance',
    'payment-ops': 'Payment Ops',
    search: 'Payment Search',
    reconciliation: 'Reconciliation',
    'standing-orders': 'Standing Orders',
    failed: 'Failed Payments',
    'scheme-routing': 'Scheme Routing',
    'smart-panel': 'Smart Panel',
    rewards: 'Rewards',
    config: 'Configuration',
    badges: 'Badges',
    offers: 'Offers',
    insights: 'Insights',
    'journey-templates': 'Journeys',
    'profile-traits': 'Profile Traits',
    branch: 'Branch Banking',
    cash: 'Cash Management',
    queue: 'Queue',
    open: 'Account Opening',
    'deposit-products': 'Deposit Products',
    'api-management': 'API Management',
    gateway: 'Gateway',
    routes: 'Routes',
    profile: 'Profile',
};

function labelForSegment(segment: string): string {
    return SEGMENT_LABELS[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface BreadcrumbItem {
    label: string;
    path: string;
}

export const BreadcrumbNav: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const crumbs = useMemo((): BreadcrumbItem[] => {
        const segments = location.pathname.split('/').filter(Boolean);
        if (segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard')) {
            return [{ label: 'Dashboard', path: '/dashboard' }];
        }

        const items: BreadcrumbItem[] = [];
        let accumulated = '';
        for (const seg of segments) {
            accumulated += `/${seg}`;
            items.push({ label: labelForSegment(seg), path: accumulated });
        }
        return items;
    }, [location.pathname]);

    return (
        <nav className="breadcrumb-nav" aria-label="Breadcrumb">
            <ol className="breadcrumb-nav__list">
                <li className="breadcrumb-nav__item">
                    <button
                        className="breadcrumb-nav__link breadcrumb-nav__home"
                        onClick={() => navigate('/dashboard')}
                        aria-label="Home"
                    >
                        <Home size={14} />
                    </button>
                </li>
                {crumbs.map((crumb, idx) => {
                    const isLast = idx === crumbs.length - 1;
                    return (
                        <li key={crumb.path} className="breadcrumb-nav__item">
                            <ChevronRight size={12} className="breadcrumb-nav__separator" />
                            {isLast ? (
                                <span className="breadcrumb-nav__current" aria-current="page">
                                    {crumb.label}
                                </span>
                            ) : (
                                <button
                                    className="breadcrumb-nav__link"
                                    onClick={() => navigate(crumb.path)}
                                >
                                    {crumb.label}
                                </button>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};

export default BreadcrumbNav;
